/**
 * Stratalogy — OpenAI Ads Pixel Helper
 *
 * Thin wrapper around the global `oaiq()` function injected by the OpenAI Ads
 * Pixel snippet in public/index.html.  Every call is guarded so the app never
 * throws if the pixel was blocked by an ad-blocker or failed to load.
 *
 * Events tracked:
 *   registration_completed — fired after a successful /auth/register call
 *   trial_started          — fired after registration (7-day free trial begins)
 *
 * Reference: OpenAI Ads conversion tracking documentation
 * Pixel ID: RnCfnTghwptCG9XLXYEJGt
 */

// ── Safe wrapper ─────────────────────────────────────────────────────────────

/** Safe wrapper — swallows errors if oaiq is not loaded (ad-blocker, etc.) */
function safeOaiq(...args) {
  try {
    if (typeof window !== 'undefined' && typeof window.oaiq === 'function') {
      window.oaiq(...args);
    }
  } catch {
    // Silently ignore — pixel tracking must never break the app
  }
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Track a successful registration (OpenAI Ads conversion).
 * Call immediately after the /auth/register API responds 200.
 *
 * Uses the "registration_completed" event with type "customer_action"
 * as specified by the OpenAI Ads pixel setup.
 */
export function trackOaiRegistration() {
  safeOaiq('measure', 'registration_completed', {
    type: 'customer_action',
    amount: 0,
    currency: 'USD',
  });
}

/**
 * Track a trial start (OpenAI Ads conversion).
 * Call immediately after registration — Stratalogy's 7-day free trial
 * begins at signup, so this fires alongside registration_completed.
 *
 * Uses the "trial_started" event with type "plan_enrollment"
 * as specified by the OpenAI Ads conversion event setup.
 */
export function trackOaiTrialStarted() {
  safeOaiq('measure', 'trial_started', {
    type: 'plan_enrollment',
  });
}

/**
 * Track a subscription / paid conversion (OpenAI Ads).
 * Call when the user completes Stripe checkout.
 *
 * @param {number} amount — dollar value of the plan (e.g. 39)
 */
export function trackOaiSubscription(amount) {
  safeOaiq('measure', 'purchase_completed', {
    type: 'customer_action',
    amount: amount || 0,
    currency: 'USD',
  });
}
